<template>
  <GlobalTopbarContainer>
    <template #left>
      <TransitionFadeInOut>
        <CreateDocumentButton
          v-if="languageLoaded && !isInactiveSubscription && isXSmallScreenAndUp"
          @create-document="$emit('create-document')"
          @create-dsr="$emit('create-dsr')"
          @created="$emit('document-created', $event)"
        />
      </TransitionFadeInOut>
      <TransitionFadeInOut>
        <Search v-if="showSearch" :collapse-enabled="isMediumScreenAndDown" class="topbar-search" />
      </TransitionFadeInOut>
    </template>
    <template #right>
      <transition-group>
        <SupportIntercom key="1" class="support-intercom" />
        <ProfileDropdown
          key="2"
          @select-entity="handleSelectEntity"
          @click="closeGlobalNav"
          @validate-md-email="showValidateMyDocumentsEmailModal = true"
        />
        <FriedIconButton
          v-if="showSideMenuSmallScreenIcon"
          key="3"
          data-external="right-sidebar-small-screen"
          class="top-bar-button right-sidebar hidden-xs-and-up"
          @click="toggleDocumentEditSidebar"
        >
          <FriedIcon icon="ellipsis" />
        </FriedIconButton>
        <FriedIconButton
          v-if="isSmallScreenAndDown"
          key="4"
          data-external="global-navigation-menu-small-screen"
          class="top-bar-button hidden-sm-and-up"
          :active="isGlobalNavMenuOpen"
          @click="toggleGlobalNavMenu"
        >
          <FriedIcon v-show="isGlobalNavMenuOpen" icon="close" size="small" />
          <FriedIcon v-show="!isGlobalNavMenuOpen" icon="hamburger-menu" />
        </FriedIconButton>
      </transition-group>
    </template>
    <MyDocumentsValidationModal
      v-if="showValidateMyDocumentsEmailModal"
      @close="showValidateMyDocumentsEmailModal = false"
    />
  </GlobalTopbarContainer>
</template>

<script lang="ts" setup>
import type { SelectEntity } from '@getaccept/lib-shared/src/select-entity/types/select-entity';
import { ResourceType } from '@getaccept/lib-shared/src/resources/types/enum/resource-type';
import { isWindowSize } from '@getaccept/lib-shared/src/helpers/window-size.helper';
import { WindowSize } from '@getaccept/lib-shared/src/enums/window-size';
import { PathName } from '@getaccept/lib-shared/src/enums/PathNames';
import { computed, ref } from 'vue';
import { storeToRefs } from 'pinia';
import useBreakpoints from '@getaccept/lib-shared/src/composables/breakpoints.composable';
import { useRoute } from 'vue-router';
import type { EnumDocumentType } from '@getaccept/lib-shared/src/enums/DocumentType';
import Search from '../search/components/Search.vue';
import { languageLoaded } from '../../lang';
import { useModulesStore } from '../../modules/store/modules.store';
import { useNavigationMenuStore } from '../store/navigation-menu.store';
import { useSubscriptionStore } from '../../subscription/store/subscription.store';
import { useAssemblerStore } from '../../document-edit/assembler/store/assembler.store';
import CreateDocumentButton from './CreateDocumentButton.vue';
import ProfileDropdown from './ProfileDropdown.vue';
import SupportIntercom from './SupportIntercom.vue';
import GlobalTopbarContainer from './GlobalTopbarContainer.vue';
import MyDocumentsValidationModal from './MyDocumentsValidationModal.vue';

const emit = defineEmits<{
  close: [];
  'document-created': [{ id: string; type: EnumDocumentType }];
  'create-dsr': [];
  'create-document': [];
  'select-entity': [SelectEntity];
}>();

const { isInactiveSubscription } = storeToRefs(useSubscriptionStore());
const { isContractRoomModuleActive } = storeToRefs(useModulesStore());
const { isOpen: isGlobalNavMenuOpen } = storeToRefs(useNavigationMenuStore());
const { toggle: toggleGlobalNavMenu, setOpen: setGlobalNavMenuOpen } = useNavigationMenuStore();

const { isMediumScreenAndDown, isSmallScreenAndUp, isSmallScreenAndDown, isXSmallScreenAndUp } =
  useBreakpoints();

const showValidateMyDocumentsEmailModal = ref(false);

const handleSelectEntity = (entity: SelectEntity) => {
  emit('select-entity', entity);
};

const closeGlobalNav = () => {
  if (isInactiveSubscription.value) return;
  setGlobalNavMenuOpen(false);
};

const assemblerStore = useAssemblerStore();

const { sidebarCollapsed } = storeToRefs(assemblerStore);

const showSearch = computed(
  () =>
    isContractRoomModuleActive.value &&
    languageLoaded.value &&
    !isInactiveSubscription.value &&
    isSmallScreenAndUp.value
);

const toggleDocumentEditSidebar = () => {
  closeGlobalNav();
  assemblerStore.setSidebarCollapsed(isWindowSize(WindowSize.Small) && !sidebarCollapsed.value);
};

const route = useRoute();
const isContentLibTemplateEdit = computed(() => {
  const pathName = route.name;
  return (
    pathName === PathName.ContentLibraryEdit &&
    route.params.type === ResourceType.Template.toLowerCase()
  );
});

const showSideMenuSmallScreenIcon = computed(() => {
  if (isXSmallScreenAndUp.value) {
    return false;
  }
  const pathName = route.name;
  return (
    pathName === PathName.DocumentEdit ||
    pathName === PathName.Template ||
    isContentLibTemplateEdit.value
  );
});
</script>

<style lang="scss" scoped>
.topbar-search {
  width: 100%;
  max-width: 24rem;
}

.top-bar-button {
  min-width: 2.5rem;
  border-radius: var(--radius-medium);
  padding: 0;

  &:active {
    background: var(--gray-94);
  }

  svg {
    fill: var(--base-blue);
  }
}
</style>
